import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function CustomerCard({ customer, counts, isMaster, onMakeMaster }) {
  if (!customer) return <div className="flex-1 bg-gray-50 rounded-xl border border-gray-200 p-5 animate-pulse h-48" />;

  return (
    <div
      className={`flex-1 rounded-xl border-2 p-5 transition-all ${
        isMaster
          ? 'border-blue-500 bg-blue-50 shadow-md'
          : 'border-gray-200 bg-white'
      }`}
    >
      <div className="flex items-start justify-between mb-3">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide mb-0.5 ${isMaster ? 'text-blue-800' : 'text-red-700'}`}>
            {isMaster ? 'Beholdes' : 'Slettes'}
          </p>
          <h3 className="text-lg font-bold text-gray-900">
            {customer.firstName} {customer.lastName}
          </h3>
        </div>
        {isMaster ? (
          <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-800">
            Beholdes
          </span>
        ) : (
          <button
            onClick={onMakeMaster}
            className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50 text-gray-600 font-medium"
          >
            Behold dette kortet
          </button>
        )}
      </div>

      <div className="space-y-1 text-sm text-gray-700 mb-4">
        <div className="flex gap-2">
          <span className="text-gray-600 w-20 flex-shrink-0">Telefon</span>
          <span className="font-mono font-medium">{customer.normalizedPhone || customer.phoneNumber}</span>
        </div>
        {customer.email && (
          <div className="flex gap-2">
            <span className="text-gray-600 w-20 flex-shrink-0">E-post</span>
            <span>{customer.email}</span>
          </div>
        )}
        {customer.customerNumber && (
          <div className="flex gap-2">
            <span className="text-gray-600 w-20 flex-shrink-0">Kundenr.</span>
            <span>{customer.customerNumber}</span>
          </div>
        )}
      </div>

      {counts && (
        <div className="grid grid-cols-3 gap-1.5">
          {[
            { label: 'Sykkelkort', n: counts.bikes },
            { label: 'Servicer', n: counts.workshopServices },
            { label: 'Tilbud', n: counts.offers },
          ].map(({ label, n }) => (
            <div key={label} className="text-center bg-white rounded-lg border border-gray-100 py-1.5">
              <p className="text-base font-bold text-gray-800">{n}</p>
              <p className="text-xs text-gray-600">{label}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function MergeCustomers() {
  const { idA, idB } = useParams();
  const navigate = useNavigate();
  // Kom fra en service (WorkshopDetails)? Gå tilbake dit etter sammenslåingen.
  const [sok] = useSearchParams();
  const fraService = /^[a-f0-9]{24}$/i.test(sok.get('fra') || '') ? sok.get('fra') : '';

  const [customerA, setCustomerA] = useState(null);
  const [customerB, setCustomerB] = useState(null);
  const [countsA, setCountsA] = useState(null);
  const [countsB, setCountsB] = useState(null);
  const [masterId, setMasterId] = useState(idA);
  const [merging, setMerging] = useState(false);
  const [error, setError] = useState('');

  const master = masterId === idA ? customerA : customerB;
  const duplicate = masterId === idA ? customerB : customerA;

  async function fetchCounts(customer) {
    if (!customer) return null;
    try {
      const [bikesRes, wsRes, offersRes] = await Promise.all([
        fetch(`${API_BASE_URL}/bikes?customerId=${encodeURIComponent(customer._id)}`),
        fetch(`${API_BASE_URL}/workshop-services?customerId=${customer._id}`),
        fetch(`${API_BASE_URL}/offers/by-customer/${customer._id}`),
      ]);
      const [bikes, ws, offers] = await Promise.all([
        bikesRes.ok ? bikesRes.json() : [],
        wsRes.ok ? wsRes.json() : [],
        offersRes.ok ? offersRes.json() : [],
      ]);
      return {
        bikes: Array.isArray(bikes) ? bikes.length : 0,
        workshopServices: Array.isArray(ws) ? ws.length : (ws?.services?.length ?? 0),
        offers: Array.isArray(offers) ? offers.length : 0,
      };
    } catch {
      return { bikes: 0, workshopServices: 0, offers: 0 };
    }
  }

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE_URL}/customers/${idA}`).then((r) => r.json()),
      fetch(`${API_BASE_URL}/customers/${idB}`).then((r) => r.json()),
    ]).then(([a, b]) => {
      setCustomerA(a);
      setCustomerB(b);
    }).catch(() => setError('Kunne ikke hente kundene.'));
  }, [idA, idB]);

  useEffect(() => {
    if (customerA) fetchCounts(customerA).then(setCountsA);
  }, [customerA]); // eslint-disable-line

  useEffect(() => {
    if (customerB) fetchCounts(customerB).then(setCountsB);
  }, [customerB]); // eslint-disable-line

  const handleMerge = async () => {
    if (!master || !duplicate) return;
    const navnTlf = (c) => `${[c.firstName, c.lastName].filter(Boolean).join(' ')} · ${c.normalizedPhone || c.phoneNumber || 'uten nummer'}`;
    const ok = window.confirm(
      `Slå sammen kundekortene?\n\n` +
      `Beholdes: ${navnTlf(master)}\n` +
      `Slettes: ${navnTlf(duplicate)}\n\n` +
      `Nummeret blir ${master.normalizedPhone || master.phoneNumber || '(ingen)'}. All historikk flyttes til kortet som beholdes. Dette kan ikke angres.`
    );
    if (!ok) return;

    setMerging(true);
    setError('');
    try {
      const res = await fetch(
        `${API_BASE_URL}/customers/${master._id}/merge/${duplicate._id}`,
        { method: 'POST' }
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || 'Feil ved sammenslåing.');
      }
      navigate(fraService ? `/workshop/${fraService}` : `/customer-details/${master._id}`);
    } catch (e) {
      setError(e.message);
      setMerging(false);
    }
  };

  if (error && !customerA && !customerB) {
    return <p className="text-red-500 text-center mt-8">{error}</p>;
  }

  return (
    <div className="max-w-3xl mx-auto py-8 px-4">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Slå sammen kundekort</h2>
        <p className="text-sm text-gray-600 mt-1">
          Velg hvilket kort som skal beholdes. Alt fra det andre kortet flyttes dit, og det andre kortet slettes.
        </p>
      </div>

      {/* Side-by-side */}
      <div className="flex flex-col sm:flex-row gap-4 mb-6">
        <CustomerCard
          customer={customerA}
          counts={countsA}
          isMaster={masterId === idA}
          onMakeMaster={() => setMasterId(idA)}
        />

        <div className="flex items-center justify-center">
          <button
            onClick={() => setMasterId((prev) => (prev === idA ? idB : idA))}
            title="Bytt hvilket kort som beholdes"
            aria-label="Bytt hvilket kort som beholdes"
            className="w-9 h-9 flex items-center justify-center rounded-full border border-gray-300 bg-white hover:bg-gray-50 text-gray-600 text-lg"
          >
            ⇄
          </button>
        </div>

        <CustomerCard
          customer={customerB}
          counts={countsB}
          isMaster={masterId === idB}
          onMakeMaster={() => setMasterId(idB)}
        />
      </div>

      {/* Hva skjer */}
      {master && duplicate && (() => {
        const navn = (c) => [c.firstName, c.lastName].filter(Boolean).join(' ') || 'Uten navn';
        const tlf = (c) => c.normalizedPhone || c.phoneNumber || '';
        const mc = duplicate === customerA ? countsA : countsB;
        const ulikNavn = navn(master).toLowerCase() !== navn(duplicate).toLowerCase();
        return (
          <div className="rounded-lg bg-gray-50 border border-gray-200 p-4 text-sm text-gray-800 mb-6">
            <p className="font-semibold mb-2">Etter sammenslåingen</p>
            <ul className="list-disc pl-5 space-y-1">
              <li>
                Kundekortet <strong>{navn(master)}</strong> beholdes, med nummer{' '}
                <strong className="font-mono">{tlf(master) || '(ingen)'}</strong>
                {master.email ? <> og e-post <strong>{master.email}</strong></> : null}.
              </li>
              <li>
                Kortet <strong>{navn(duplicate)} · {tlf(duplicate) || 'uten nummer'}</strong> slettes.
              </li>
              <li>
                Flyttes til kortet som beholdes:{' '}
                {mc ? `${mc.workshopServices} servicer, ${mc.bikes} sykkelkort, ${mc.offers} tilbud` : 'servicer, sykkelkort, tilbud'}
                , og samtykker, dekk- og sykkelhotell, bookinger og SMS- og e-posthistorikk.
              </li>
              {tlf(duplicate) && tlf(duplicate) !== tlf(master) && (
                <li>
                  <span className="font-mono">{tlf(duplicate)}</span> lagres som tidligere nummer. Meldinger om saker
                  registrert med det, går til <span className="font-mono">{tlf(master) || 'kortet som beholdes'}</span>.
                </li>
              )}
            </ul>
            {ulikNavn && (
              <p className="mt-3 text-amber-900 bg-amber-50 border border-amber-200 rounded p-2">
                Navnene er ulike. Slå bare sammen hvis det er samme person.
              </p>
            )}
            <p className="mt-2 text-gray-600">Vil du beholde det andre nummeret, trykk «Behold dette kortet» på det andre kortet.</p>
          </div>
        );
      })()}

      {error && <p className="text-red-500 text-sm mb-4">{error}</p>}

      <div className="flex items-center gap-3">
        <Link
          to={fraService ? `/workshop/${fraService}` : `/customer-details/${idA}`}
          className="px-5 py-2.5 rounded border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm"
        >
          Avbryt
        </Link>
        <button
          onClick={handleMerge}
          disabled={merging || !master || !duplicate}
          className="px-6 py-2.5 rounded bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50"
        >
          {merging
            ? 'Slår sammen…'
            : `Slå sammen – behold ${master?.normalizedPhone || master?.phoneNumber || 'kortet'}`}
        </button>
      </div>
    </div>
  );
}

export default MergeCustomers;
